import { createContext, useContext, useEffect, useState, ReactNode } from 'react';

type Theme = 'light' | 'dark';

interface ThemeContextType {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
  useSystemTheme: () => void;
  isSystemTheme: boolean;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
  defaultTheme?: Theme;
}

export function ThemeProvider({ children, defaultTheme }: ThemeProviderProps) {
  const [source, setSource] = useState<'user' | 'system'>(() => {
    return localStorage.getItem('tereiko-theme') ? 'user' : 'system';
  });

  const [theme, setThemeState] = useState<Theme>(() => {
    const stored = localStorage.getItem('tereiko-theme') as Theme | null;
    if (stored) return stored;
    if (defaultTheme) return defaultTheme;
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  });

  const setTheme = (newTheme: Theme) => {
    setSource('user');
    setThemeState(newTheme);
  };

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e: MediaQueryListEvent) => {
      if (source === 'system') {
        setThemeState(e.matches ? 'dark' : 'light');
      }
    };
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [source]);

  useEffect(() => {
    if (source === 'user') {
      localStorage.setItem('tereiko-theme', theme);
    }

    const root = document.documentElement;
    root.classList.remove('light', 'dark');
    root.classList.add(theme);
  }, [theme, source]);

  const toggleTheme = () => {
    setTheme(theme === 'light' ? 'dark' : 'light');
  };

  const useSystemTheme = () => {
    localStorage.removeItem('tereiko-theme');
    setSource('system');
    const systemDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    setThemeState(systemDark ? 'dark' : 'light');
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme, useSystemTheme, isSystemTheme: source === 'system' }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
